/**
 * Una scadenza complessiva per le fetch della catena dei token.
 *
 * 🔴 IL FATTO [M 2026-09-06, or45 su stg-tma + lettura del sorgente]. Una giocatrice reale ha
 * visto la Hub Eventi restare **in attesa per minuti** con il Laravel di staging giu'. La
 * causa non e' il 503 — un 503 e' una risposta, e la app lo sa gestire: e' una richiesta che
 * **non riceve risposta affatto**.
 *
 * `services/api.ts:30` fa `await getApiAccessToken()` **prima** di emettere la fetch vera.
 * Dentro, la catena e':
 *
 *   getApiAccessToken (`logto.ts:1587`) → exchangeLogtoForBackend (`:1531`)
 *     → runBackendExchange (`:1546`) → getLogtoAccessToken (`:534`)
 *       → refreshLogtoToken (`:600`) → redeemRefreshToken → doRedeem (`:831`)
 *         → **`await fetch(tokenUrl, …)` a `:845`**
 *   e sull'altro ramo: postExchange (`:1482`) → **`await fetch(exchangeUrl, …)` a `:1497`**
 *
 * Nessuna di quelle due fetch aveva una scadenza. E la misura piu' larga: in **tutto**
 * `logto.ts` ci sono sei `await fetch` e **zero** occorrenze di `AbortController` /
 * `AbortSignal` / `signal:`. L'unica protezione esistente — `SDK_TOKEN_TIMEOUT_MS` (`:296`,
 * usata a `:528`) — copre il getter dell'SDK, cioe' l'unico ramo che una fetch nostra non la
 * fa. ⇒ se una fetch della catena non settla, **ogni chiamata API dell'app resta ferma in
 * `prepareHeaders` per sempre**, e nessuna schermata puo' dirlo: non c'e' errore, non c'e'
 * risposta, c'e' un `await` che non torna.
 *
 * ⚠️ Peggiorato dal single-flight: chi arriva dopo si aggancia alla **stessa** promessa
 * (`inFlightBackendExchange`, `refreshChain`) e resta appeso con lei. Verificato eseguendo la
 * catena ridotta con una fetch che non risolve mai: due chiamanti, entrambi pendenti dopo 3 s.
 *
 * ## 🔑 Perche' LANCIA invece di ritornare `null`
 *
 * I due chiamanti hanno gia' un `catch` che produce esattamente l'esito voluto — «la rete e'
 * caduta»: `doRedeem` ritorna `{accessToken: null, expiresIn: 0}`, `postExchange` ritorna
 * `{token: null, status: 0, code: null}`, e uno `status: 0` non e' fra i
 * `TERMINAL_EXCHANGE_CODES`, quindi arma `backendRefusedUntil` per la finestra breve come
 * farebbe un 503.
 *
 * Lanciare significa quindi che la scadenza **non introduce un ramo nuovo**: riusa quello che
 * gia' esiste e che e' gia' stato pensato. Ritornare `null` avrebbe voluto dire toccare i due
 * chiamanti per distinguere un caso che non va distinto — e ogni ramo in piu' su questa catena
 * e' un ramo che qualcuno dovra' capire mentre insegue un utente fermo.
 *
 * ⛔ E soprattutto: la scadenza **non e' un `invalid_grant`**. Non incrementa
 * `consecutiveInvalidGrants`, non chiama `handleDeadGrant`, non cancella la sessione. Una rete
 * lenta non e' un grant morto, e confonderli farebbe di questa cura una macchina di logout —
 * esattamente cio' contro cui #144 e #151 esistono.
 */

/**
 * Quanto si aspetta una fetch della catena dei token prima di dichiararla persa.
 *
 * ⚠️ **E' una SCELTA, non una misura** — ma poggia su una misura. `POST /oidc/token` su
 * staging, 16 campioni letti dai `network.json` gia' registrati dal runner e2e (nessun
 * traffico nuovo): **min 107 ms · mediana 373 ms · p90 908 ms · max 980 ms**.
 *
 * 15 s sono ~40× la mediana e ~15× il p90: non possono accendersi su un refresh sano ma
 * lento, nemmeno su una rete mobile pessima dentro Telegram. La regola «≥10× la mediana»
 * darebbe 3,7 s, che e' solo ~4× il massimo osservato — troppo vicino per una costante che,
 * se sbaglia, butta via una sessione buona.
 */
export const TOKEN_FETCH_TIMEOUT_MS = 15_000;

/** L'errore della scadenza. Ha un nome perche' nei log si distingua da una rete caduta vera. */
export class TokenFetchTimeoutError extends Error {
  // ⚠️ Campo dichiarato e assegnato a mano, NON una parameter property
  // (`constructor(public readonly timeoutMs: number)`). Node 24 esegue questo file in
  // **type-stripping**: cancella i tipi e non trasforma nulla, e una parameter property e'
  // una trasformazione. Il gate esce con
  // `ERR_UNSUPPORTED_TYPESCRIPT_SYNTAX: TypeScript parameter property is not supported in
  // strip-only mode` — e siccome esce all'IMPORT, il file non stampa niente e l'exit code e'
  // 0: sembra un test che non c'e', non un test rotto. E' il patto di `verify-limulo.ts`
  // (nessuna dipendenza, nessun transpilatore) applicato a una sintassi che di solito passa.
  readonly timeoutMs: number;

  constructor(timeoutMs: number) {
    super(`richiesta della catena dei token scaduta dopo ${timeoutMs} ms`);
    this.name = 'TokenFetchTimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

export interface FetchLike {
  (input: string, init?: RequestInit): Promise<Response>;
}

/**
 * Esegue una fetch con una scadenza complessiva. Alla scadenza **aborta** la richiesta e
 * lancia `TokenFetchTimeoutError`, che i `catch` esistenti trattano come una rete caduta.
 *
 * `fetchImpl` e la fabbrica dell'`AbortController` arrivano come PARAMETRI: e' cio' che
 * rende il file esercitabile sotto Node dal gate — con una fetch che non risolve mai, che e'
 * proprio il caso che nessun test potrebbe scrivere se la dipendenza fosse catturata.
 */
export async function fetchWithTimeout(
  fetchImpl: FetchLike,
  input: string,
  init: RequestInit,
  timeoutMs: number = TOKEN_FETCH_TIMEOUT_MS,
  newController: () => AbortController = () => new AbortController(),
): Promise<Response> {
  const controller = newController();
  let scaduta = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  // 🔴 SI CORRE, non si aspetta soltanto l'abort — e questa riga e' nata da un test che ha
  // bocciato la prima stesura.
  //
  // La prima versione faceva `await fetchImpl(..., {signal})` e si fidava che l'abort
  // facesse rifiutare la fetch. **Il `fetch` del browser lo fa**, quindi in produzione
  // sarebbe bastata. Ma il guardiano esiste proprio per il caso «questa chiamata non
  // risponde»: farlo dipendere dalla COLLABORAZIONE della cosa da cui ci si difende e' la
  // premessa sbagliata. Il gate l'ha dimostrato in un secondo — con una fetch che ignora il
  // segnale, `fetchWithTimeout` restava appesa esattamente come il codice che cura.
  //
  // Quindi due cose insieme, e servono entrambe:
  //  · la CORSA garantisce che la promessa settli comunque, chiunque sia la fetch;
  //  · l'ABORT resta perche' la corsa da sola lascerebbe la connessione aperta e il server a
  //    lavorare per un risultato che nessuno leggera'. Una scadenza che libera il chiamante
  //    ma non la rete e' mezza cura.
  const scadenza = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      scaduta = true;
      controller.abort();
      reject(new TokenFetchTimeoutError(timeoutMs));
    }, timeoutMs);
  });

  try {
    return await Promise.race([fetchImpl(input, { ...init, signal: controller.signal }), scadenza]);
  } catch (err) {
    // ⚠️ Si distingue l'abort NOSTRO da un errore altrui. Un errore di rete vero deve arrivare
    // al chiamante COM'E': i due casi portano allo stesso esito oggi, ma un log che dice
    // «scaduta» su una connessione rifiutata manda a cercare la lentezza dove c'e' un rifiuto.
    if (scaduta) throw new TokenFetchTimeoutError(timeoutMs);
    throw err;
  } finally {
    // ⚠️ Sempre, anche sul cammino felice: un timer che sopravvive alla risposta abortirebbe
    // una richiesta gia' conclusa — innocuo — ma terrebbe vivo il processo nei test e il
    // callback in memoria per 15 s a ogni chiamata.
    clearTimeout(timer);
  }
}
